import React, { useState } from 'react'
import { PropTypes } from 'prop-types'
import { isFunction, flowRight } from 'lodash'
import classNames from 'classnames'
import { withTranslationCustom } from 'lib/i18next'
import { logException } from 'lib/errors'

import Dropzone from 'react-dropzone'
import Icon from 'components/Icon'

import './extended-dropzone.pcss'

export const ExtendedDropzoneContext = React.createContext()

const ExtendedDropzone = ({
    t,
    children,
    className,
    allowedTypes,
    onFileSelected,
    maxSize,
    allowMultiple,
    renderInputComponent,
    noClick,
}) => {
    const [isCamera, setIsCamera] = useState(false)

    const renderOverlay = (props) => {
        const { isDragReject, isDragAccept } = props

        return (
            <div
                className={classNames('extended-dropzone__overlay', {
                    'extended-dropzone__overlay--accept': isDragAccept,
                    'extended-dropzone__overlay--reject': isDragReject,
                })}
            >
                <div className="extended-dropzone__overlay-content">
                    <Icon name="icon_check_white" size="xxl" />
                    <span>
                        {t('imageUpload:dropImageFile')}
                    </span>
                </div>
            </div>
        )
    }

    return (
        <Dropzone
            accept={isCamera ? 'image/*' : allowedTypes}
            maxSize={maxSize}
            multiple={false}
            onDrop={(acceptedFiles, fileRejections) => {
                if (fileRejections.length > 0) return false

                if (isCamera) {
                    acceptedFiles = acceptedFiles.map(file => {
                        const re = /(?:\.([^.]+))?$/
                        try {
                            const extension = re.exec(file.name)[1]
                            const blob = file.slice(0, file.size, file.type)
                            // eslint-disable-next-line no-undef
                            const newFile = new File([blob], `${t('imageUpload:cameraImage')}.${extension}`, { type: file.type })
                            return newFile
                        } catch (error) {
                            logException(error)
                            return file
                        }
                    })
                }

                acceptedFiles.forEach(file => onFileSelected(file))
            }}
            allowMultiple={allowMultiple}
            noClick={noClick}
        >
            {(props) => {
                const {
                    getRootProps,
                    getInputProps,
                    isDragActive,
                } = props

                return (
                    <ExtendedDropzoneContext.Provider value={{ ...props, setCamera: setIsCamera }}>
                        <div className={`extended-dropzone ${className}`} {...getRootProps()}>
                            {renderInputComponent && <input {...getInputProps()} capture={isCamera && 'environment'} />}
                            {isDragActive && renderOverlay(props)}
                            {isFunction(children) ? children(props) : children}
                        </div>
                    </ExtendedDropzoneContext.Provider>
                )
            }}
        </Dropzone>
    )
}

ExtendedDropzone.propTypes = {
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
    renderInputComponent: PropTypes.bool.isRequired,
    allowedTypes: PropTypes.object.isRequired,
    onFileSelected: PropTypes.func.isRequired,
    maxSize: PropTypes.number.isRequired,
    allowMultiple: PropTypes.bool.isRequired,
    noClick: PropTypes.bool,
    className: PropTypes.string,
}

ExtendedDropzone.defaultProps = {
    renderInputComponent: true,
}

export default flowRight(
    withTranslationCustom('imageUpload'),
)(ExtendedDropzone)
